import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Ban, CheckCircle2, Edit, X, Save, Eye, TrendingUp, Award, HandCoins, Clock } from "lucide-react";
import { TierChip } from "@/components/TierBadge";

const EMPTY = {
  full_name: "", birth_date: "", nif: "", address: "", postal_code: "",
  locality: "", phone: "", email: "", payment_method: "Cartão Dá",
};

const STAGE_LABEL = {
  recommended: "Recomendado", interview: "Entrevista", selected: "Selecionado",
  started: "Iniciou funções", validated: "Período mínimo",
  rejected: "Rejeitado", withdrawn: "Desistiu", blacklisted: "Blacklist",
};

function DetailModal({ referrerId, onClose }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!referrerId) return;
    api.get(`/referrers/${referrerId}/history`).then(({ data }) => setData(data));
  }, [referrerId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4">
      <div className="bg-white rounded-3xl w-full max-w-4xl max-h-[92vh] flex flex-col shadow-xl">
        <div className="p-6 flex items-center justify-between border-b border-[var(--border)]">
          <div>
            <div className="eyebrow">Detalhe do referenciador</div>
            <h2 className="font-heading text-2xl flex items-center gap-2">
              {data?.referrer?.full_name || "…"}
              {data?.referrer?.code && <span className="text-sm text-[var(--ink-soft)] font-mono">{data.referrer.code}</span>}
              {data?.tier && <TierChip tier={data.tier}/>}
            </h2>
          </div>
          <button onClick={onClose} className="pill-btn pill-ghost !py-2 !px-3" data-testid="close-referrer-detail"><X className="w-4 h-4"/></button>
        </div>
        <div className="p-6 overflow-y-auto space-y-6">
          {!data ? <div className="text-center py-10 text-[var(--ink-soft)]">A carregar…</div> : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="card-surface p-4 text-center"><TrendingUp className="w-4 h-4 text-[var(--info)] mx-auto"/><div className="font-heading text-2xl mt-1">{data.stats.recommended}</div><div className="text-xs text-[var(--ink-soft)]">Recomendados</div></div>
                <div className="card-surface p-4 text-center"><Award className="w-4 h-4 text-[var(--success)] mx-auto"/><div className="font-heading text-2xl mt-1">{data.stats.validated}</div><div className="text-xs text-[var(--ink-soft)]">Contratações validadas</div></div>
                <div className="card-surface p-4 text-center"><HandCoins className="w-4 h-4 text-[var(--terracotta)] mx-auto"/><div className="font-heading text-2xl mt-1">{Number(data.stats.bonuses_total_paid).toFixed(0)} €</div><div className="text-xs text-[var(--ink-soft)]">Total recebido</div></div>
                <div className="card-surface p-4 text-center"><Ban className="w-4 h-4 text-[var(--ink-soft)] mx-auto"/><div className="font-heading text-2xl mt-1">{data.stats.excluded}</div><div className="text-xs text-[var(--ink-soft)]">Excluídos</div></div>
              </div>

              <div>
                <div className="eyebrow mb-3 flex items-center gap-1.5"><Clock className="w-3 h-3"/> Histórico de recomendações</div>
                {data.candidates.length === 0 ? (
                  <div className="text-center py-8 text-[var(--ink-soft)]">Este referenciador ainda não recomendou candidatos.</div>
                ) : (
                  <div className="border border-[var(--border)] rounded-2xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-[var(--muted)] text-left">
                        <tr>
                          <th className="px-3 py-2">Data</th>
                          <th className="px-3 py-2">Candidato</th>
                          <th className="px-3 py-2">Vaga</th>
                          <th className="px-3 py-2">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.candidates.map((c) => {
                          const excluded = ["rejected", "withdrawn", "blacklisted"].includes(c.stage);
                          return (
                            <tr key={c.id} className="border-t border-[var(--border)]" data-testid={`history-cand-${c.id}`}>
                              <td className="px-3 py-2 text-xs text-[var(--ink-soft)]">{new Date(c.created_at).toLocaleDateString("pt-PT")}</td>
                              <td className="px-3 py-2">
                                <div className="font-medium">{c.name}</div>
                                <div className="text-xs text-[var(--ink-soft)]">{c.email}</div>
                              </td>
                              <td className="px-3 py-2 text-xs">{c.vacancy_title}</td>
                              <td className="px-3 py-2">
                                <span className={`chip ${excluded ? "chip-terra" : c.stage === "validated" ? "chip-green" : "chip-blue"}`}>{STAGE_LABEL[c.stage] || c.stage}</span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {data.bonuses.length > 0 && (
                <div>
                  <div className="eyebrow mb-3 flex items-center gap-1.5"><HandCoins className="w-3 h-3"/> Prémios</div>
                  <div className="border border-[var(--border)] rounded-2xl overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-[var(--muted)] text-left">
                        <tr>
                          <th className="px-3 py-2">Data</th>
                          <th className="px-3 py-2">Tipo</th>
                          <th className="px-3 py-2">Valor</th>
                          <th className="px-3 py-2">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.bonuses.map((b) => (
                          <tr key={b.id} className="border-t border-[var(--border)]">
                            <td className="px-3 py-2 text-xs text-[var(--ink-soft)]">{new Date(b.created_at).toLocaleDateString("pt-PT")}</td>
                            <td className="px-3 py-2 text-xs">{b.type === "badge" ? <span className="chip chip-terra">Emblema {b.badge_label || ""}</span> : <span className="chip chip-neutral">Base</span>}</td>
                            <td className="px-3 py-2 font-mono">{Number(b.amount).toFixed(2)} €</td>
                            <td className="px-3 py-2"><span className={`chip ${b.status === "paid" ? "chip-green" : "chip-neutral"}`}>{b.status}</span></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function AdminReferrers() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/referrers").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const toggle = async (r) => {
    try {
      const nextBlocked = !r.is_blocked;
      const isInactivity = r.suspension_reason === "inactivity";
      if (!nextBlocked && isInactivity) {
        const ok = window.confirm(
          `Reativar ${r.full_name}? A conta foi suspensa por inatividade (${r.days_inactive} dias sem recomendar). O contador de inatividade será reiniciado.`,
        );
        if (!ok) return;
      }
      await api.patch(`/referrers/${r.id}/block`, { is_blocked: nextBlocked });
      toast.success(
        nextBlocked
          ? "Referenciador bloqueado"
          : isInactivity ? "Referenciador reativado — inatividade reiniciada" : "Referenciador desbloqueado",
      );
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openEdit = (r) => setEditing({ ...EMPTY, ...r });
  const closeEdit = () => setEditing(null);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { ...editing };
      const id = body.id;
      ["id","_id","code","user_id","protocol_signed","protocol_signature","is_blocked","created_at"].forEach((k) => delete body[k]);
      await api.patch(`/referrers/${id}`, body);
      toast.success("Referenciador atualizado");
      closeEdit();
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const inputCls = "w-full px-3 py-2.5 rounded-xl bg-[var(--muted)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--forest)]";

  return (
    <div className="space-y-6">
      <div>
        <div className="eyebrow">Gestão</div>
        <h1 className="font-heading text-5xl mt-2">Referenciadores</h1>
      </div>
      <div className="card-surface overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[var(--muted)] text-left">
              <th className="px-4 py-3">Código</th>
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Contactos</th>
              <th className="px-4 py-3">Protocolo</th>
              <th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-t border-[var(--border)]" data-testid={`referrer-row-${r.id}`}>
                <td className="px-4 py-3 font-mono text-xs">{r.code}</td>
                <td className="px-4 py-3">
                  <div className="font-medium">{r.full_name}</div>
                  <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                    {r.tier && <TierChip tier={r.tier}/>}
                    {r.stats && (
                      <span className="text-[10px] text-[var(--ink-soft)]">
                        {r.stats.recommended} recomendados · {r.stats.validated} validados · {Number(r.stats.total_earned || 0).toFixed(0)} €
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-xs text-[var(--ink-soft)]">{r.email}<br/>{r.phone}</td>
                <td className="px-4 py-3">
                  {r.protocol_signed ? (
                    <span className="chip chip-green"><CheckCircle2 className="w-3 h-3"/> Assinado</span>
                  ) : <span className="chip chip-neutral">Pendente</span>}
                </td>
                <td className="px-4 py-3">
                  {r.is_blocked ? (
                    <div className="space-y-1">
                      <span className={`chip ${r.suspension_reason === "inactivity" ? "chip-terra" : "chip-terra"}`}>
                        {r.suspension_reason === "inactivity" ? "Suspenso (inatividade)" : "Bloqueado"}
                      </span>
                      {r.suspension_reason === "inactivity" && (
                        <div className="text-[10px] text-[var(--ink-soft)]">{r.days_inactive} dias sem recomendar</div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <span className="chip chip-green">Ativo</span>
                      {typeof r.days_inactive === "number" && r.days_inactive >= 60 && (
                        <div
                          className={`text-[10px] ${r.days_inactive >= 75 ? "text-[var(--terracotta)]" : "text-[var(--ink-soft)]"}`}
                          data-testid={`ref-inactivity-warn-${r.id}`}
                        >
                          {r.days_inactive >= 75
                            ? `Aviso: ${r.days_inactive}d sem recomendar (susp. em ${Math.max(0, 90 - r.days_inactive)}d)`
                            : `${r.days_inactive}d sem recomendar`}
                        </div>
                      )}
                    </div>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-1">
                    <button onClick={() => setDetailId(r.id)} className="pill-btn pill-ghost !py-1.5 !px-3" data-testid={`detail-ref-${r.id}`}>
                      <Eye className="w-3.5 h-3.5"/> Detalhe
                    </button>
                    <button onClick={() => openEdit(r)} className="pill-btn pill-ghost !py-1.5 !px-3" data-testid={`edit-ref-${r.id}`}>
                      <Edit className="w-3.5 h-3.5"/> Editar
                    </button>
                    <button onClick={() => toggle(r)} className="pill-btn pill-ghost !py-1.5 !px-3" data-testid={`toggle-ref-${r.id}`}>
                      <Ban className="w-3.5 h-3.5"/> {r.is_blocked ? (r.suspension_reason === "inactivity" ? "Reativar" : "Desbloquear") : "Bloquear"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-[var(--ink-soft)]">Sem referenciadores.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm grid place-items-center p-4" onClick={closeEdit}>
          <form onSubmit={save} className="bg-white rounded-3xl w-full max-w-2xl overflow-hidden max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 flex items-center justify-between border-b border-[var(--border)] sticky top-0 bg-white z-10">
              <div>
                <div className="eyebrow">Editar referenciador</div>
                <h2 className="font-heading text-2xl">{editing.full_name} <span className="text-sm text-[var(--ink-soft)] font-mono ml-2">{editing.code}</span></h2>
              </div>
              <button type="button" onClick={closeEdit} className="pill-btn pill-ghost !py-2 !px-3" data-testid="close-edit-ref"><X className="w-4 h-4"/></button>
            </div>
            <div className="p-6 grid md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <label className="text-sm font-medium">Nome completo</label>
                <input required value={editing.full_name} onChange={(e) => setEditing({ ...editing, full_name: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="edit-ref-full-name"/>
              </div>
              <div>
                <label className="text-sm font-medium">Data de nascimento</label>
                <input type="date" value={editing.birth_date} onChange={(e) => setEditing({ ...editing, birth_date: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="edit-ref-birth-date"/>
              </div>
              <div>
                <label className="text-sm font-medium">NIF</label>
                <input value={editing.nif} onChange={(e) => setEditing({ ...editing, nif: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="edit-ref-nif"/>
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium">Morada</label>
                <input value={editing.address} onChange={(e) => setEditing({ ...editing, address: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="edit-ref-address"/>
              </div>
              <div>
                <label className="text-sm font-medium">Código Postal</label>
                <input value={editing.postal_code} onChange={(e) => setEditing({ ...editing, postal_code: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="edit-ref-postal"/>
              </div>
              <div>
                <label className="text-sm font-medium">Localidade</label>
                <input value={editing.locality} onChange={(e) => setEditing({ ...editing, locality: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="edit-ref-locality"/>
              </div>
              <div>
                <label className="text-sm font-medium">Telefone</label>
                <input value={editing.phone} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="edit-ref-phone"/>
              </div>
              <div>
                <label className="text-sm font-medium">Email</label>
                <input type="email" value={editing.email} onChange={(e) => setEditing({ ...editing, email: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="edit-ref-email"/>
              </div>
              <div>
                <label className="text-sm font-medium">Método de pagamento</label>
                <input value={editing.payment_method} onChange={(e) => setEditing({ ...editing, payment_method: e.target.value })} className={`mt-1.5 ${inputCls}`} data-testid="edit-ref-payment"/>
              </div>
            </div>
            <div className="p-6 border-t border-[var(--border)] flex justify-end gap-2 sticky bottom-0 bg-white">
              <button type="button" onClick={closeEdit} className="pill-btn pill-ghost">Cancelar</button>
              <button disabled={busy} className="pill-btn pill-primary" data-testid="edit-ref-save-btn">
                <Save className="w-4 h-4"/> {busy ? "A guardar…" : "Guardar"}
              </button>
            </div>
          </form>
        </div>
      )}
      {detailId && <DetailModal referrerId={detailId} onClose={() => setDetailId(null)}/>}
    </div>
  );
}
